import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Footer,
  CountInfo,
  RangeLetters,
  ProgressWrap,
  FloatingLetter,
  FloatingLetterWrap,
  ProgressInput,
} from './styled'

interface KeywordsFilterFooterProps {
  listRef: React.RefObject<HTMLDivElement>
  letters: string[]
  keywordCountRange: { minCount: number; maxCount: number }
}

const KeywordsFilterFooter = ({
  listRef,
  letters,
  keywordCountRange,
}: KeywordsFilterFooterProps) => {
  const { t } = useTranslation()
  const [scrollProgress, setScrollProgress] = useState(0)
  const [activeLetter, setActiveLetter] = useState('A')
  const [beforeLetter, setBeforeLetter] = useState<string | null>(null)
  const [afterLetter, setAfterLetter] = useState<string | null>(null)
  const [isAtEnd, setIsAtEnd] = useState(false)

  const updateScrollState = useCallback(() => {
    const list = listRef.current
    if (!list) return

    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth)
    const progress = maxScroll > 0 ? (list.scrollLeft / maxScroll) * 100 : 0

    const viewportCenter = list.scrollLeft + list.clientWidth / 2
    let activeIndex = 0

    // The list's direct children are the keyword columns, in order
    Array.from(list.children).forEach((child, index) => {
      const column = child as HTMLElement
      const columnCenter = column.offsetLeft + column.clientWidth / 2
      if (columnCenter <= viewportCenter) {
        activeIndex = index
      }
    })

    // If scrolled to the very end, always surface the last letter
    const atEnd = maxScroll > 0 && list.scrollLeft >= maxScroll - 5
    const actualIndex = atEnd ? letters.length - 1 : activeIndex
    const currentLetter = atEnd
      ? letters[letters.length - 1] || 'A'
      : letters[activeIndex] || letters[0] || 'A'

    setIsAtEnd(atEnd)
    setScrollProgress(atEnd ? 100 : progress)
    setActiveLetter(currentLetter)
    setBeforeLetter(actualIndex > 0 ? letters[actualIndex - 1] : null)
    setAfterLetter(actualIndex < letters.length - 1 ? letters[actualIndex + 1] : null)
  }, [listRef, letters])

  // Reset the indicator whenever the letter set changes (e.g. language switch)
  useEffect(() => {
    setActiveLetter(letters[0] || 'A')
    setBeforeLetter(null)
    setAfterLetter(null)
    setIsAtEnd(false)
    setScrollProgress(0)
  }, [letters])

  // Subscribe to the list's scroll directly so a scroll only re-renders the footer,
  // not the full keyword list. rAF-throttled to at most one update per frame.
  useEffect(() => {
    const list = listRef.current
    if (!list) return

    let frame = 0
    const handleScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        updateScrollState()
      })
    }

    list.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      list.removeEventListener('scroll', handleScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [listRef, updateScrollState])

  const handleProgressChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    const nextProgress = Number(e.target.value)
    setScrollProgress(nextProgress)

    const list = listRef.current
    if (!list) return

    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth)
    list.scrollLeft = (nextProgress / 100) * maxScroll
    updateScrollState()
  }

  const progressStyle = {
    ['--progress' as const]: scrollProgress / 100,
  } as React.CSSProperties

  return (
    <Footer>
      <CountInfo hidden>
        min: {keywordCountRange.minCount} | max: {keywordCountRange.maxCount}
      </CountInfo>
      <RangeLetters $hidden={activeLetter === letters[0]}>
        {t('keywordsFilter.rangeLettersFirst')}
      </RangeLetters>
      <ProgressWrap style={progressStyle}>
        <FloatingLetterWrap>
          {beforeLetter && <FloatingLetter $size="small">{beforeLetter}</FloatingLetter>}
          {isAtEnd ? (
            <FloatingLetter $size="normal">{activeLetter}</FloatingLetter>
          ) : (
            <>
              <FloatingLetter $size="normal">{activeLetter}</FloatingLetter>
              {afterLetter && <FloatingLetter $size="small">{afterLetter}</FloatingLetter>}
            </>
          )}
        </FloatingLetterWrap>
        <ProgressInput
          type="range"
          min={0}
          max={100}
          step={0.1}
          value={scrollProgress}
          onChange={handleProgressChange}
          aria-label="Keyword letter progress"
        />
      </ProgressWrap>
      <RangeLetters $hidden={activeLetter === letters[letters.length - 1]}>
        {t('keywordsFilter.rangeLettersLast')}
      </RangeLetters>
    </Footer>
  )
}

export default React.memo(KeywordsFilterFooter)
